import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPageTitle from '../components/AdminPageTitle';
import api from '../services/api';
import './ProductForm.css';

const ProductNew = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subtitle: '',
    price: '',
    stock: '',
    status: 'active',
    featured: false,
    availability_type: 'available_locally',
  });
  const [mainImage, setMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalImagesPreview, setAdditionalImagesPreview] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [productSpecificationText, setProductSpecificationText] = useState('');
  const [errors, setErrors] = useState({});
  const additionalImagesInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setMainImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAdditionalImages(prev => [...prev, ...files]);
      
      // Create previews for all files
      const previewPromises = files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
      });
      
      // Update previews when all files are read
      Promise.all(previewPromises).then(previews => {
        setAdditionalImagesPreview(prev => [...prev, ...previews]);
      });
    }
    
    // Reset input so same file can be selected again
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleAddMoreImages = () => {
    if (additionalImagesInputRef.current) {
      additionalImagesInputRef.current.click();
    }
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
    setAdditionalImagesPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSpecificationChange = (index, field, value) => {
    const newSpecs = [...specifications];
    newSpecs[index] = {
      ...newSpecs[index],
      [field]: value
    };
    setSpecifications(newSpecs);
  };

  const handleAddSpecification = () => {
    setSpecifications(prev => [
      ...prev,
      { id: Date.now(), spec_name: '', spec_value: '', price: '', available: true }
    ]);
  };

  const handleRemoveSpecification = (index) => {
    setSpecifications(prev => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Product description is required';
    }
    if (!mainImage && additionalImages.length === 0) {
      newErrors.images = 'At least one product image is required';
    }

    // Validate specifications
    specifications.forEach((spec, index) => {
      if (!spec.spec_name.trim()) {
        newErrors[`spec_name_${index}`] = 'Specification name is required';
      }
      if (!spec.spec_value.trim()) {
        newErrors[`spec_value_${index}`] = 'Specification value is required';
      }
      if (!spec.price || parseFloat(spec.price) < 0) {
        newErrors[`spec_price_${index}`] = 'Valid price is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Add basic product data
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      if (formData.subtitle) submitData.append('subtitle', formData.subtitle);
      if (formData.price) submitData.append('price', formData.price);
      if (formData.stock) submitData.append('stock', formData.stock);
      submitData.append('status', formData.status);
      submitData.append('featured', formData.featured ? '1' : '0');
      submitData.append('availability_type', formData.availability_type || '');

      // Add main image
      if (mainImage) {
        submitData.append('image', mainImage);
      }

      // Add additional images
      additionalImages.forEach((image) => {
        submitData.append('images[]', image);
      });

      // Add variants (previously called specifications)
      if (specifications.length > 0) {
        specifications.forEach((spec, index) => {
          submitData.append(`specifications[${index}][spec_name]`, spec.spec_name);
          submitData.append(`specifications[${index}][spec_value]`, spec.spec_value);
          submitData.append(`specifications[${index}][price]`, spec.price);
          submitData.append(`specifications[${index}][available]`, spec.available ? '1' : '0');
        });
      }

      // Add rich product specification (technical/spec sheet) as JSON list (one item per line)
      if (productSpecificationText.trim()) {
        const lines = productSpecificationText
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        lines.forEach((line) => {
          submitData.append('product_specification[]', line);
        });
      }

      // For FormData, we need to pass it directly without JSON.stringify
      const response = await api.post('/admin/products', submitData);
      
      if (response.data) {
        alert('Product created successfully!');
        navigate('/admin/products');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      alert(error.response?.data?.message || 'Failed to create product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="product-form-page">
      <AdminPageTitle title="New Product" />
      <div className="page-header">
        <h1>Add New Product</h1>
        <button onClick={() => navigate('/admin/products')} className="back-btn">
          ← Back to Products
        </button>
      </div>

      <form onSubmit={handleSubmit} className="product-form" encType="multipart/form-data">
        <div className="form-section">
          <h2>Basic Information</h2>
          
          <div className="form-group">
            <label htmlFor="name">Product Title *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'error' : ''}
              placeholder="Enter product title"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="subtitle">Product Subtitle</label>
            <input
              type="text"
              id="subtitle"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              placeholder="Enter product subtitle"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Product Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="5"
              className={errors.description ? 'error' : ''}
              placeholder="Enter product description"
            ></textarea>
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">Base Price (¥)</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="0.00 (Yuan)"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="stock">Available Stock *</label>
              <input
                type="number"
                id="stock"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                placeholder="0"
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
              />
              <span>Mark as Featured Product</span>
            </label>
            <p className="field-help-text">Featured products will be highlighted on the homepage</p>
          </div>

          <div className="form-group">
            <label htmlFor="availability_type">Availability Type</label>
            <select
              id="availability_type"
              name="availability_type"
              value={formData.availability_type}
              onChange={handleChange}
            >
              <option value="">— Not set —</option>
              <option value="shipped_abroad">Shipped from Abroad</option>
              <option value="available_locally">Available Locally</option>
              <option value="for_export">For Export</option>
            </select>
            <p className="field-help-text">Shows a badge on the storefront for this product.</p>
          </div>
        </div>

        <div className="form-section">
          <h2>Product Images</h2>
          
          <div className="form-group">
            <label htmlFor="mainImage">Main Product Image</label>
            <input
              type="file"
              id="mainImage"
              name="mainImage"
              accept="image/*"
              onChange={handleMainImageChange}
            />
            {mainImagePreview && (
              <div className="image-preview">
                <img src={mainImagePreview} alt="Main preview" />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Additional Product Images</label>
            <div className="image-upload-section">
              <input
                type="file"
                id="additionalImages"
                name="additionalImages"
                accept="image/*"
                multiple
                onChange={handleAdditionalImagesChange}
                ref={additionalImagesInputRef}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={handleAddMoreImages}
                className="add-more-images-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add More Images
              </button>
              {additionalImages.length > 0 && (
                <span className="images-count">
                  {additionalImages.length} image{additionalImages.length !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            {errors.images && <span className="error-message">{errors.images}</span>}
            
            {additionalImagesPreview.length > 0 && (
              <div className="images-preview-grid">
                {additionalImagesPreview.map((preview, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => removeAdditionalImage(index)}
                      className="remove-image-btn"
                      title="Remove image"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <div className="section-header-inline">
            <h2>Product Variants</h2>
            <button type="button" onClick={handleAddSpecification} className="add-size-btn">
              + Add Variant
            </button>
          </div>

          {specifications.length > 0 ? (
            <div className="sizes-list">
              {specifications.map((spec, index) => (
                <div key={spec.id || index} className="size-item-form">
                  <div className="form-group">
                    <label>Variant Name *</label>
                    <input
                      type="text"
                      value={spec.spec_name}
                      onChange={(e) => handleSpecificationChange(index, 'spec_name', e.target.value)}
                      placeholder="e.g., Size, Weight, Color"
                      className={errors[`spec_name_${index}`] ? 'error' : ''}
                    />
                    {errors[`spec_name_${index}`] && (
                      <span className="error-message">{errors[`spec_name_${index}`]}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Variant Value *</label>
                    <input
                      type="text"
                      value={spec.spec_value}
                      onChange={(e) => handleSpecificationChange(index, 'spec_value', e.target.value)}
                      placeholder="e.g., 50 x 10 (sqm), 2kg, Red"
                      className={errors[`spec_value_${index}`] ? 'error' : ''}
                    />
                    {errors[`spec_value_${index}`] && (
                      <span className="error-message">{errors[`spec_value_${index}`]}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Variant Price (¥) *</label>
                    <input
                      type="number"
                      value={spec.price}
                      onChange={(e) => handleSpecificationChange(index, 'price', e.target.value)}
                      placeholder="0.00 (Yuan)"
                      min="0"
                      step="0.01"
                      className={errors[`spec_price_${index}`] ? 'error' : ''}
                    />
                    {errors[`spec_price_${index}`] && (
                      <span className="error-message">{errors[`spec_price_${index}`]}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={spec.available !== false}
                        onChange={(e) => handleSpecificationChange(index, 'available', e.target.checked)}
                      />
                      Available
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveSpecification(index)}
                    className="remove-size-btn"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-sizes">No variants added. Click \"Add Variant\" to add product variants.</p>
          )}
        </div>

        <div className="form-section">
          <h2>Product Specification (Technical Details)</h2>
          <div className="form-group">
            <label htmlFor="productSpecification">
              Product Specification (one item per line)
            </label>
            <textarea
              id="productSpecification"
              name="productSpecification"
              rows="6"
              value={productSpecificationText}
              onChange={(e) => setProductSpecificationText(e.target.value)}
              placeholder={
                'Material: Durable polyethylene (PE)\n' +
                'UV Treatment: Up to 5 years\n' +
                'Thickness: 150, 200, 220 microns\n' +
                'Light Transmission: 85-90%\n' +
                'Anti-Drip: Yes'
              }
            ></textarea>
            <p className="field-help-text">
              Each line will be saved as a separate specification item in a JSON list.
            </p>
          </div>
        </div>

        <div className="form-actions">
          <div className="action-buttons">
            <button type="button" onClick={() => navigate('/admin/products')} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Creating...' : 'Create Product'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductNew;
